import api from "@/lib/api";
import { isAxiosError } from "axios";
import { getStoredCurrentEnvironment } from "@/lib/environment";

function managedOperationKey(jobId: string, action: string, revision: number) {
  const scope = JSON.stringify([getStoredCurrentEnvironment()?.id ?? "", jobId, action, revision]);
  const storageKey = `forex-managed-request:${scope}`;
  let key: string | null = null;
  try { key = sessionStorage.getItem(storageKey); } catch { /* Storage may be disabled. */ }
  key ||= managedRequestKeys.get(scope) || crypto.randomUUID();
  managedRequestKeys.set(scope, key);
  try { sessionStorage.setItem(storageKey, key); } catch { /* Keep the in-memory key. */ }
  return {key, complete: () => {
    managedRequestKeys.delete(scope);
    try { sessionStorage.removeItem(storageKey); } catch { /* No persisted key. */ }
  }};
}
const managedRequestKeys = new Map<string, string>();

type ListResponse<T> = {
  items: T[];
  total: number;
};

type Envelope<T> = {
  success?: boolean;
  data: T;
};

export interface ForexOperationsOverviewCore {
  generated_at: number;
  scope: "forex-admin";
  registered_users?: number | null;
  linked_follower_accounts?: number | null;
  registration_trend?: {
    coverage_started_at_ms: number;
    timezone: "Asia/Shanghai";
    points: Array<{ date: string; count: number | null; coverage: "full" | "partial" | "unavailable" }>;
  } | null;
  follower_users: number;
  active_relations: number;
  authorized_accounts: number;
  listed_agents: number;
  attention: {
    generated_at: number;
    scope_hash: string;
    total: number;
    completeness: "complete";
    categories: Record<string, { count: number; status: "ready"; generated_at: number }>;
  };
}

export interface ForexOperationsOverviewEquity {
  schema_version: "OperationsOverviewEquityV1";
  scope_hash: string;
  snapshot_hash?: string | null;
  account_environment: "simulation" | "real";
  evaluated_at: number;
  target_platform_accounts: number;
  covered_platform_accounts: number;
  excluded_platform_accounts: number;
  unknown_platform_accounts: number;
  reason_counts: Record<string, number>;
  currencies: Array<{ currency: string; state: "ready" | "partial"; value: string | null;
    known_value: string; covered_physical_accounts: number; covered_platform_accounts: number; observed_at: number }>;
}

export interface ForexOperationsOverviewCoverage {
  schema_version: "OperationsOverviewCoverageV1";
  metric: "equity";
  scope_hash: string;
  snapshot_hash: string;
  account_environment: "simulation" | "real";
  evaluated_at: number;
  total: number;
  items: Array<{ account_id: string; reason_code: string }>;
  next_cursor: string | null;
}

export interface ForexOperationsOverviewRuntime {
  schema_version: "OperationsOverviewRuntimeV1";
  scope_hash: string;
  account_environment: "simulation" | "real";
  evaluated_at: number;
  overall_state: "partial";
  terminal: { state: "ready" | "partial" | "empty"; health: "healthy" | "attention" | "unknown";
    target_platform_accounts: number; applicable_platform_accounts: number;
    online_count: number; stale_count: number; offline_count: number; unknown_count: number;
    unknown_environment_count: number; excluded_environment_count: number;
    stale_after_seconds: 30; reason_counts: Record<string, number> };
  unavailable_domains: Array<"market" | "decision" | "execution">;
}

export interface ForexOperationsOverviewDecisionObserved {
  schema_version: "OperationsOverviewDecisionObservedV1";
  scope_hash: string;
  evaluated_at: number;
  from_utc: number;
  to_utc: number;
  source: "forex_master_signal_runs";
  source_environment: "demo";
  state: "partial" | "unavailable";
  history_status: "observed_only";
  total_observed: number;
  status_counts: Record<string, number>;
  latest_updated_at: number | null;
  reason_codes: string[];
}

export interface ForexOperationsOverviewMarketObserved {
  schema_version: "OperationsOverviewMarketObservedV1";
  scope_hash: string;
  evaluated_at: number;
  source: "platform_collector";
  route_revision: number;
  state: "partial" | "unavailable";
  status: "fresh" | "stale" | "paused" | "unavailable";
  symbol_count: number;
  fresh_symbol_count: number;
  stale_symbol_count: number;
  last_received_at: number | null;
  reason_codes: string[];
}

export interface ForexOperationsOverviewExecutionObserved {
  schema_version: "OperationsOverviewExecutionObservedV1";
  scope_hash: string;
  evaluated_at: number;
  from_utc: number;
  to_utc: number;
  source: "forex_master_execution_commands";
  source_scope: "all_account_environments";
  state: "partial" | "unavailable";
  total_observed: number;
  status_counts: Record<string, number>;
  latest_updated_at: number | null;
  reason_codes: string[];
}

export interface ForexOperationsOverviewPerformance {
  schema_version: "OperationsOverviewPerformanceV1";
  scope_hash: string;
  evaluated_at: number;
  timezone: "Asia/Shanghai";
  range: "today" | "7d" | "30d";
  account_role: "master" | "follower";
  account_environment: "simulation" | "real";
  currency: string | null;
  from_utc: number;
  to_utc: number;
  time_rules_hash: string;
  fills: { state: "partial" | "unavailable"; value: null;
    known_opened: number; known_closed: number; duplicate_transmissions: number;
    conflicting_keys: number; unknown_identity_events: number;
    unknown_time_events: number; history_status: "unverified";
    reason_codes: string[];
    observed_daily: Array<{ date: string; known_opened: number | null; known_closed: number | null }> };
}

export interface ForexOperationsOverviewFollowObserved {
  schema_version: "OperationsOverviewFollowObservedV1";
  scope_hash: string;
  evaluated_at: number;
  timezone: "Asia/Shanghai";
  range: "today" | "7d" | "30d";
  from_utc: number;
  to_utc: number;
  history_status: "unverified";
  label: "first_observed_follow";
  points: Array<{ date: string; count: number | null }>;
  reason_codes: string[];
}

export interface ForexOperationsOverviewAgentPage {
  generated_at: number;
  scope: "forex-admin";
  items: Array<{ agent_id: string; name: string; follower_users: number; active_relations: number;
    configured_symbols?: string[] | null; desired_state?: "running" | "stopped" | null;
    observed_demo_decisions_24h?: number | null }>;
  total: number;
  page: number;
  page_size: number;
  decision_observation_state?: "partial" | "unavailable" | null;
  decision_observed_from_utc?: number | null;
  decision_observed_to_utc?: number | null;
}

export interface ForexOperatorTodoPage {
  items: Array<{ id: string; kind: "agent_review" | "follower_review" | "follower_material" | "follower_provision_blocked" | "execution_unknown" | "instance_maintenance";
    subject_id: string; updated_at: number; title: string; reason_code: string | null }>;
  next_cursor: string | null;
  counts: ForexOperationsOverviewCore["attention"];
  generated_at: number;
  scope_hash: string;
}

export function managedMaintenanceError(error: unknown): string {
  const code=isAxiosError(error) ? error.response?.data?.detail?.code : undefined;
  if (typeof code !== "string") return "MT4 维护请求未完成，请刷新状态后重试";
  const messages:Record<string,string>={
    AUTO_ACCOUNT_EXECUTION_UNRESOLVED:"账号仍有持仓、挂单或在途执行",
    AUTO_ACCOUNT_IN_USE:"当前实例或 Collector 来源仍在使用",
    AUTO_CAPACITY_FULL:"当前节点没有可用启动容量",
    AUTO_EXECUTION_STATE_UNKNOWN:"账号执行状态未知，请先核对连接、持仓与挂单",
    AUTO_RUNNER_UNAVAILABLE:"Windows Runner 不在线，请恢复节点后重试",
    AUTO_RUNNER_PROTOCOL_UNSUPPORTED:"节点 Runner 尚未升级到维护协议版本",
    AUTO_SOURCE_CHANGED:"行情来源或供数状态已变化，请刷新后重新选择操作",
    AUTO_COLLECTOR_CONSUMERS_CHANGED:"行情消费者已变化，请刷新后重新选择操作",
    AUTO_COLLECTOR_REQUIRED_DATA_NOT_READY:"所需行情数据尚未恢复，请检查缺口后重试",
    AUTO_OPERATION_IN_PROGRESS:"当前实例正在维护，请等待或查看检查结果",
    AUTO_INSTANCE_FILES_MISSING:"实例目录缺失，请检查后选择重建",
    AUTO_RETRY_BLOCKED:"此操作不可继续重试，请检查当前阶段和失败原因",
    AUTO_STALE_REVISION:"账号状态已变化，请刷新后重试",
    AUTO_INSTANCE_OWNER_CONFLICT:"MT4 进程身份核对失败",
    AUTO_IDEMPOTENCY_CONFLICT:"本次操作的内容与原请求不一致",
  };
  return `${messages[code] || "维护条件未满足"}（${code}）`;
}

export type ForexStatus = "enabled" | "disabled";

export interface AtomicModel {
  atomic_model_id: string;
  provider: string;
  model_id: string;
  display_name: string;
  purpose: string;
  monthly_limit: number;
  cost_per_1k_tokens: number;
  latency_ms: number;
  status: ForexStatus;
  bound_agent_count: number;
  updated_at: number;
}

export interface StrategyParameters {
  common_rules: Record<string, string | number>;
  family_rules: Record<string, string | number>;
}

async function strategyConfirmation(confirmedInPage = false): Promise<string> {
  const environment = getStoredCurrentEnvironment();
  if (!environment?.id) throw new Error("ENV_HEADER_REQUIRED");
  if (!confirmedInPage && !window.confirm(`确认在 ${environment.display_name} 保存策略及账户风控？`)) throw new Error("RISK_CONFIRMATION_CANCELLED");
  const response = await api.post("/quantum/business/risk/confirmations", {
    env_id: environment.id, operation_key: "forex.strategy.update",
  });
  const token = response.data?.data?.confirmation_token;
  if (typeof token !== "string" || !token) throw new Error("RISK_CONFIRMATION_UNAVAILABLE");
  return token;
}

export interface StrategyTemplatePatchPayload {
  expected_row_revision: number;
  expected_version: number;
  name?: string;
  category?: string;
  preset_key?: string;
  symbols?: string[];
  description?: string;
  ai_decision_style?: "conservative" | "balanced" | "proactive";
  catalog_hash?: string;
  defaults_ref?: ImmutableArtifactRef;
  parameters?: StrategyParameters;
}

export interface FollowerAccountReview {
  platform?: 'mt4' | 'mt5' | null;
  review_id: string;
  follower_account_id: string;
  user_ref: string;
  broker: string;
  server: string;
  mt4_login_masked: string;
  account_status: "reviewing" | "ea_provisioning" | "connected" | "rejected" | "need_material";
  usage_status: string;
  terminal_status: "pending" | "creating" | "none" | string;
  submitted_at: number;
  updated_at: number;
  reason?: string | null;
  submission_revision?: number | null;
  review_revision?: number | null;
  reviewed_by?: string | null;
  reviewed_at?: number | null;
}

export interface TestAccountAccess {
  follower_account_id: string;
  owner_scope: string;
  enabled: boolean;
  revision: number;
  identity_valid: boolean;
  updated_by: string;
  updated_at: number;
}

export interface FollowerReviewPatchPayload {
  decision: "approve" | "reject" | "request_material";
  reason?: string;
  expected_submission_revision?: number;
  expected_review_revision?: number;
}

export type InstanceAction = "inspect" | "pause" | "resume" | "restart" | "cleanup" | "rebuild" | "start" | "retry_maintenance";
export interface InstanceLifecycle {
  runtime_recovery?: {state: string; checked_at: number; generation: number} | null;
  supply_restore?: {state: string; reason_code?: string} | null;
  data_readiness?: {scope: string; state: string; required_windows: number; ready_windows: number; reason_codes: string[]; checked_at?: number; spread_progress?: Array<{symbol: string; sample_count: number; required_count: number; last_sample_at?: number | null}>} | null;
  recovery_readiness?: {scope: string; state: string; required_windows: number; ready_windows: number; reason_codes: string[]} | null;
  drain_pending?: Record<string,number>;
  supported: boolean; display_state: string; resource_state: string;
  operation: {id: string; action: string; status: string; phase: string; phase_sequence: number; error_code?: string | null; started_at: number; deadline_at: number; retry_count: number} | null;
  process: {process_state?: string; received_at?: number};
  available_actions: Array<{action: InstanceAction; enabled: boolean; reason_code: string | null}>;
  source_revision?: number | null; collector_revision?: number | null; supply_state?: string | null;
}

export interface MaintenanceCompatibility {
  supported: boolean; reason_code: string | null; original_pause_resume_supported?: boolean; original_state?: string;
}
export interface ProvisioningJob {
  diagnostics?: {
    stage: string; blocker_code: string | null;
    owner: 'account' | 'platform' | 'node' | 'operator' | 'none';
    next_action: 'wait' | 'resubmit_credentials' | 'retry' | 'contact_operator' | 'none';
    retryable: boolean; dependency_revision: number; updated_at: number;
  };
  platform?: 'mt4' | 'mt5' | null;
  maintenance_compatibility?: MaintenanceCompatibility | null;
  shared_collector?: {collector_id:string;status:string;supported:boolean} | null;
  lifecycle?: InstanceLifecycle | null;
  generation?: number; config_revision?: number; attempt?: number;
  events?: Array<{event_id: string; event_type: string; created_at: number; from_status?: string | null; to_status?: string | null; config_revision?: number; actor_ref?: string | null; operation_id?: string | null}>;
  execution_mode?: "managed_v1"; automation_stage?: string; revision?: number; can_retry?: boolean; can_cancel?: boolean; last_error_code?: string | null;
  runner_id?: string | null; maintenance_state?: "running" | "quiescing" | "stopped" | "resuming" | null;
  provision_job_id: string;
  target_type: "master_account" | "follower_account" | string;
  target_id: string;
  account_role: "master" | "follower" | string;
  terminal_id: string;
  broker: string;
  server: string;
  account_number_masked: string;
  ea_version: string;
  supported_symbols: string[];
  expected_account_environment: string;
  enable_live_order_send: boolean;
  enable_command_poll: boolean;
  enable_quote_push: boolean;
  status: string;
  created_at: number;
  updated_at: number;
  events_total?: number;
  events_next_cursor?: number | null;
  events_next_page_token?: string | null;
  history_complete?: boolean;
  history_reason_code?: string;
  history_retention_days?: number;
  history_from?: number;
  history_as_of?: number;
  events_total_reason_code?: string;
  heartbeat?: Record<string, unknown> | null;
  last_error?: string | null;
  simulation_attestation?: {
    status: string;
    attested_environment: string;
    evidence_ref: string;
    reason: string;
    attested_by: string;
    runtime_account_environment?: string | null;
    runtime_account_trade_mode?: string | null;
    created_at: number;
  } | null;
}

export interface ProvisioningJobCreatePayload {
  execution_mode?: "managed_v1"; credential_binding_id?: string; expected_credential_version?: number; capability_profile?: string;
  target_type: "master_account" | "follower_account";
  target_id: string;
  account_role: "master" | "follower";
  broker?: string;
  server?: string;
  account_number_masked?: string;
  terminal_id?: string;
  ea_version?: string;
  expected_account_environment?: string;
  supported_symbols?: string[];
}

export interface ProvisioningSimulationAttestationPayload {
  attested_environment: string;
  evidence_ref: string;
  reason: string;
  attested_by?: string;
}

export interface MasterAccount {
  platform?: 'mt4' | 'mt5' | null;
  credential_binding_id?: string; credential_version?: number; provisioning_mode?: "managed_v1";
  master_account_id: string;
  broker: string;
  server: string;
  account_number_masked: string;
  credential_ref: string;
  supported_symbols: string[];
  status: string;
  connection_status: string;
  terminal_count: number;
  updated_at: number;
}

export interface MasterAccountCreatePayload {
  platform?: 'mt4' | 'mt5';
  broker: string;
  server: string;
  account_number: string;
  credential_ref: string;
  supported_symbols: string[];
}

export interface MasterAccountVerifyPayload {
  platform: 'mt4' | 'mt5';
  broker: string;
  server: string;
  account_number: string;
  credential_ref: string;
  supported_symbols: string[];
}

export interface MasterAccountVerifyResult {
  verified: boolean;
  broker: string;
  server: string;
  account_number_masked: string;
  supported_symbols: string[];
  connection_status: string;
  verified_at: number;
  message: string;
}

export interface MasterAccountPatchPayload {
  status?: string;
  supported_symbols?: string[];
}

export interface MasterTradeOrder {
  order_ref: string;
  master_account_id: string;
  account_number_masked: string;
  broker_ticket_masked: string;
  symbol: string;
  action: string;
  lot?: string | number | null;
  open_price?: string | number | null;
  current_price?: string | number | null;
  close_price?: string | number | null;
  stop_loss?: string | number | null;
  take_profit?: string | number | null;
  pnl?: string | number | null;
  commission?: string | number | null;
  swap?: string | number | null;
  lifecycle_status: string;
  link_status: string;
  opened_at: number;
  opened_at_utc?: number | null;
  closed_at_utc?: number | null;
  last_event_at_utc?: number | null;
  closed_at?: number | null;
  last_event_at: number;
  freshness_status: "fresh" | "stale" | "final" | string;
  sync_status?: "fresh" | "stale" | "error" | "unverified";
  last_successful_sync_at?: number | null;
  pnl_as_of?: number | null;
  time_status?: string;
  protection_status?: string | null;
  signal_id?: string | null;
  agent_id?: string | null;
  master_order_event_id?: string | null;
  source_mtf_run_id?: string | null;
  execution_source?: "simulation_no_broker" | "reported_unverified" | "master_fill_fact";
}

export interface MasterTradeOrderPage extends ListResponse<MasterTradeOrder> {
  time_unverified_count?: number;
  next_cursor?: string | null;
}

export interface MasterTradeTrace {
  order: MasterTradeOrder;
  events: Array<Record<string, unknown>>;
  follower_orders: Array<Record<string, unknown>>;
  follower_summary: Record<string, number>;
}

export interface OperatorOwner {
  owner_ref: string;
  name: string;
  status: "active" | "suspended" | "pending" | string;
  review_status: "approved" | "pending" | "rejected" | string;
  contact_person: string;
  master_account_count: number;
  active_follow_relations: number;
  total_pnl: number;
  joined_at: number;
  updated_at: number;
  status_reason?: string | null;
  node_type?: 'company' | 'team' | null;
  parent_owner_ref?: string | null;
  company_ref?: string | null;
}

export interface TeamMember {
  member_ref: string;
  member_no: string;
  name: string;
  owner_ref: string;
  company_ref: string;
  status: 'active' | 'inactive';
  revision: number;
  code: string | null;
  invitation_ref: string | null;
  invited_count?: number | null;
}

export interface InvitedUser {
  user_id: string;
  email_masked: string | null;
  owner_ref: string;
  company_ref: string;
  path_names: string[];
  recorded_at: number;
  inviter_kind: 'member' | 'legacy_team';
  inviter_member_ref: string | null;
  inviter_name_snapshot: string | null;
}

export interface OperatorOwnerPatchPayload {
  status: "active" | "suspended";
  reason?: string;
}

export interface ModelInvocationLog {
  log_id: string;
  agent_id: string;
  agent_name: string;
  role_key: string;
  provider: string;
  model_id: string;
  request_type: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  latency_ms: number;
  cost: number;
  status: "success" | "fallback" | "error" | string;
  error_message?: string | null;
  fallback_model_id?: string | null;
  created_at: number;
}

export interface RegisteredDataSource {
  source_id: string;
  name: string;
  capability: "market" | "macro" | "execution" | string;
  authority_level: string;
  freshness_seconds: number;
  status: ForexStatus;
  used_for_execution: boolean;
  provider: string;
  health: {
    latency_ms: number;
    available_rate: number;
    [key: string]: unknown;
  };
}

export interface ForexTeamSelection {
  schema_version: "ForexTeamSelectionV1";
  mode: "single" | "multi";
  analyst_role_ids: string[];
  expected_catalog_hash: string;
}

export interface MasterSignalListItem {
  signal_summary: {
    signal_id: string;
    agent_id: string;
    symbol: string;
    action: string;
    config_version: number;
    generated_at: number;
    valid_until: number;
    validation_status: string;
  };
  status: string;
}

export interface MasterSignalDetail {
  signal_summary: MasterSignalListItem["signal_summary"];
  stages: Array<{ stage_id: string; label: string; status: string; summary: string; started_at?: number; completed_at?: number }>;
  accounts: Array<{
    target_id: string; target_role: "platform_master" | "user_follower";
    account_scope: string; target_state: string; readiness_reason: string | null;
    command_id: string | null; command_status: string | null;
    result: { phase?: string; order_effect?: string; broker_ticket_masked?: string } | null;
    created_at: number; updated_at: number;
  }>;
  downstream: {
    status: string;
    label: string;
    existing_detail_ref?: string | null;
    follower_commands?: Array<{
      target_id: string;
      account_scope: string;
      command_id: string | null;
      target_state: string;
      command_status: string | null;
      result_phase: string | null;
    }>;
  };
}

export type StrategySignalList = { items: MasterSignalListItem[]; next_cursor: string | null };

export interface MasterSignalRunSummary {
  run_id: string;
  agent_id: string;
  symbol: string;
  timeframe: string;
  status: string;
  error_code?: string | null;
  attempt: number;
  created_at: number;
  updated_at: number;
}

export interface MasterSignalRunDetail {
  run: MasterSignalRunSummary & Record<string, unknown>;
  lineage: { package_hash?: string | null; runtime_policy_hash?: string | null; binding_hash?: string | null };
  package?: Record<string, unknown> | null;
  artifacts: Record<string, Array<{ artifact_key: string; payload: Record<string, unknown>; payload_hash: string }>>;
  artifact_count: number;
}

export interface TerminalSlot {
  terminal_id: string;
  master_account_id: string;
  ea_type: string;
  status: "online" | "paused" | "restarting" | string;
  last_heartbeat_at: number;
  latency_ms: number;
  updated_at: number;
  status_reason?: string | null;
}

export interface TerminalPatchPayload {
  action: "pause" | "resume" | "restart";
  reason?: string;
}

export interface RiskConfig {
  risk_config_id: string;
  scope: string;
  daily_loss_limit: number;
  weekly_loss_limit: number;
  max_lot_per_trade: number;
  max_total_lots: number;
  kill_switch: "on" | "off";
  updated_at: number;
}

export interface RiskConfigPatchPayload {
  daily_loss_limit?: number;
  weekly_loss_limit?: number;
  max_lot_per_trade?: number;
  max_total_lots?: number;
  kill_switch?: "on" | "off";
  reason?: string;
}

export interface ConfigRelease {
  release_id: string;
  config_key: string;
  payload: Record<string, unknown>;
  description: string;
  status: "active" | "superseded" | "rolled_back" | string;
  version: string;
  published_at: number;
  rolled_back_at?: number | null;
}

export interface ConfigReleaseCreatePayload {
  config_key: string;
  payload: Record<string, unknown>;
  description?: string;
}

export interface ConfigPackage {
  config_key: string;
  label: string;
  description: string;
  payload: Record<string, unknown>;
  status: ForexStatus;
  version: string;
  last_published_at?: number | null;
  api_path: string;
  updated_at: number;
}

export interface ConfigPackagePatchPayload {
  payload?: Record<string, unknown>;
  description?: string;
  status?: ForexStatus;
}

export interface ConfigPackagePublishPayload {
  description?: string;
}

export interface ConfigPackagePublishResult {
  package: ConfigPackage;
  release: ConfigRelease;
}

export interface KillSwitchPatchPayload {
  scope?: string;
  target: "on" | "off";
  reason: string;
}

export interface KillSwitchResult {
  scope: string;
  kill_switch: "on" | "off";
  reason: string;
  updated_at: number;
  affected_agents: number;
  affected_follow_relations: number;
}

export interface FollowRelation {
  relation_id: string;
  user_ref: string;
  follower_account_id: string;
  follower_name: string;
  follower_account_masked: string;
  agent_id: string;
  agent_name: string;
  broker: string;
  server: string;
  symbols: string[];
  copy_mode: "fixed_lot" | "proportional" | "fixed_ratio" | string;
  copy_value: number;
  max_drawdown: number;
  status: "active" | "paused" | "stopped" | string;
  opened_at: number;
  opened_at_utc?: number | null;
  closed_at_utc?: number | null;
  last_event_at_utc?: number | null;
  current_positions: number;
  total_pnl: number;
  success_rate: number;
  last_command_id: string;
  updated_at: number;
  status_reason?: string | null;
}

export interface FollowRelationPatchPayload {
  action: "pause" | "resume" | "stop";
  reason?: string;
}

export interface ExecutionSymbolReadiness {
 symbol:string;status:'ready'|'waiting_quote'|'blocked';reason_code:string|null;reason_codes:string[];
 observed_at:number;quote_time_utc:number|null;quote_received_at:number|null;config_revision:number|null;
}
export interface EAExecutionReadiness {
  execution_readiness?:ExecutionSymbolReadiness[];
  terminal_id: string; job_id: string; account_id: string; revision: number; config_revision: number;
  ea_version: string; state: string; connection_status: string; effective_for_new_opens: boolean;
  blocked_reasons: string[]; operation_id?: string | null;
  certification_ref: {id:string;revision:string;hash:string}; time_rule_ref: {id:string;revision:string;hash:string};
}

export interface AccountCommandEvidence {
  generated_at: number; completeness: "partial";
  scope: {platform: string; generation: number | null; delivery_epoch: number | null; account_fingerprint_ref: string | null};
  received_at: number | null; permit_recorded: boolean | null; permit_expires_at: number | null;
  result: {event_id: string | null; result_hash: string | null; observed_at_utc: number | null};
  broker: {verification: "unverified"; references: Array<{kind: string; masked_ref: string}>};
  projection: {state: "available" | "pending_projection" | "unverified"; orders: Array<{order_ref: string; observed_at: number}>; overflow: boolean};
  missing: string[];
}
export interface AccountCommand {
  command_id: string; target_id: string; source_signal_id: string; agent_id: string;
  account_scope: string; terminal_id: string; action: "OPEN" | "CLOSE" | "MODIFY_PROTECTION";
  symbol: string; side: "BUY" | "SELL" | null;
  requested_lot: string | null; authorized_lot: string | null; target_remaining_lot: string | null;
  created_at: number; submit_deadline: number; updated_at: number; status: string;
  result_phase: string | null; order_effect: string | null; actual_lot: string | null;
  error_code: string | null;
  evidence?: AccountCommandEvidence | null;
}

export interface AccountExecutionIssue {
  issue_id: string; issue_kind: "target" | "command"; target_id: string | null;
  command_id: string | null; signal_id: string; account_scope: string;
  account_role: "platform_master" | "user_follower"; terminal_id: string | null;
  agent_id: string; symbol: string; state: string; reason_code: string | null;
  result_phase: string | null; order_effect: string | null;
  created_at: number; updated_at: number;
}

export interface ImmutableArtifactRef {
  id: string;
  version: number;
  content_hash: string;
}

export interface ForexAuditLog {
  audit_id: string;
  action: string;
  target_id: string;
  detail: Record<string, unknown>;
  operator?: string;
  target_type?: string;
  request_id?: string;
  source_ip?: string;
  result?: string;
  risk_level?: string;
  before_state?: Record<string, unknown> | null;
  after_state?: Record<string, unknown> | null;
  created_at: number;
}

export interface ForexDashboard<T> {
  summary: Record<string, number | string>;
  items: T[];
}

export type TemplateSelectionV1 = { strategy: ImmutableArtifactRef | null; risk_hash: string | null };

export interface ConfigAgentV1 {
  active_template_selection?: TemplateSelectionV1 | null;
  automation?: {
    phase: "review_required" | "stopped" | "preparing" | "partial_failure" | "waiting_trigger" | "running";
    reason_codes: string[];
    updated_at: number;
    accounts: Array<{ account_id: string; role: "master" | "follower"; stage: "ready" | "preparing" | "blocked"; reasons: string[] }>;
  };
  agent_id: string;
  name: string;
  master_account_id: string;
  terminal_id: string;
  lifecycle: "available" | "deleting";
  publication: "hidden" | "listed";
  active_config_id: string | null;
  row_revision: number;
  draft_revision: number;
  draft_hash: string;
  runtime: "stopped" | "running";
  control_revision: number;
  created_at: number;
  updated_at: number;
}

export interface ConfigReviewV1 {
  template_changes?: { before: TemplateSelectionV1 | null; after: TemplateSelectionV1 | null };
  review_id: string;
  agent_id: string;
  config_id: string;
  status: "pending" | "approved" | "rejected" | "superseded";
  expected_active_config_id: string | null;
  source_draft_revision: number;
  submitted_by: string;
  submitted_at: number;
  frozen_config?: { config_version: number; content_hash: string; payload: Record<string, unknown> };
  previous_config?: { config_version: number; content_hash: string; payload: Record<string, unknown> } | null;
}

export interface ConfigAgentDeletionPreview {
  agent_id: string;
  name: string;
  master_account_id: string;
  row_revision: number;
  lifecycle: "available" | "deleting";
  delete_counts: { configs: number; reviews: number; targets?: number; reservations?: number };
  blockers?: Record<string, number>;
  inflight_count: number;
  can_start: boolean;
}

export interface StrategyHistoryRequest {
  schema_version: "StrategySpecV1";
  strategy_key: string;
  timeframe_config: StrategyTimeframeLayout;
  parameters: Record<string, string | number>;
}

export interface StrategyHistoryPreview {
  schema_version: "HistoryRequirementsPreviewV1";
  strategy_key: string;
  normalized_spec_hash: string;
  history_plan_hash: string | null;
  history_policy_revision: string;
  closed_bar_counts: Record<string, number>;
  data_roles: Record<string, string>;
  quality: "finite_exact" | "seed_weight_bounded" | "finite_state_budget";
  capacity: { max_closed_bars_per_role: number };
  breakdown: Array<{ node_id: string; required_bars: number | null; required_by_role?: Record<string, number> }>;
}

export interface StrategyTimeframeLayout {
  mode: "single" | "multi";
  trigger_timeframe: string;
  data_roles: { signal: string; context?: string; structure?: string };
}

export interface ConfigCatalogV1 {
  strategies: { schema_version: "StrategyCatalogV2"; items: Array<{
    strategy_key: string; name: string; category: string; supported_modes: string[];
    supported_role_counts?: number[]; supported_layouts?: StrategyTimeframeLayout[];
    trigger_timeframes: string[]; defaults: Record<string, string | number>; required_closed_bars: number;
    parameters: { properties: Record<string, { title?: string; type: string; minimum?: number; maximum?: number; default?: string | number }> };
  }> };
  risk: { items: Array<{ content: Record<string, unknown> }> };
  teams: { catalog_hash: string; roles: Array<{ role_id: string; name: string; kind: string }> };
  cadences: { default_profile_id: string; items: Array<{ profile_id: string; label: string }> };
  strategy_fields: Array<{ path: string; label: string; description: string; type: string;
    unit?: string | null; min?: string | number | null; max?: string | number | null;
    step?: string | number | null; applicable_categories?: string[]; readonly?: boolean }>;
  role_prompt_defaults: Array<{ role_id: string; instruction: string }>;
}

export interface ConfigCompositionV1 {
  strategy_template_ref: ImmutableArtifactRef;
  platform_execution_target: { account_scope: string; terminal_ref: string | null };
}

export interface ConfigTemplateV1 {
  current_revision?: number;
  editable_payload?: Record<string, unknown> | null;
  changed?: boolean;
  template_ref: ImmutableArtifactRef;
  kind: "strategy";
  name: string;
  row_revision: number;
  selectable: boolean;
  research_available?: boolean;
  retained_version?: boolean;
  qualification?: { qualified: boolean; reason: string; scope_hashes?: string[];
    completed_stages?: string[]; assessment_scope_hash?: string | null;
    assessment_scope?: { symbol?: string; timeframe?: string; model_id?: string; source_ref?: { stream_id?: string } } | null };
  payload: Record<string, unknown>;
  created_at: number;
  updated_at: number;
}

const FOREX_PREFIX = "/quantum/business/forex";

export function configAgentError(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: { code?: string; message?: string } } } })
    ?.response?.data?.detail;
  return detail?.code ? `${detail.message || fallback}（${detail.code}）` : fallback;
}

function unwrap<T>(response: { data: Envelope<T> | T }): T {
  const payload = response.data as Envelope<T>;
  return "data" in payload ? payload.data : (response.data as T);
}

export const forexApi = {
  async getOperationsOverviewCore(): Promise<ForexOperationsOverviewCore> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/core`));
  },

  async getOperationsOverviewEquity(accountEnvironment: "simulation" | "real"): Promise<ForexOperationsOverviewEquity> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/equity`,
      { params: { account_environment: accountEnvironment } }));
  },

  async getOperationsOverviewCoverage(accountEnvironment: "simulation" | "real",
    snapshotHash: string, cursor?: string): Promise<ForexOperationsOverviewCoverage> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/coverage`,
      { params: { metric: "equity", account_environment: accountEnvironment,
        snapshot_hash: snapshotHash, limit: 20, ...(cursor ? { cursor } : {}) } }));
  },

  async getOperationsOverviewRuntime(accountEnvironment: "simulation" | "real"): Promise<ForexOperationsOverviewRuntime> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/runtime`,
      { params: { account_environment: accountEnvironment } }));
  },

  async getOperationsOverviewPerformance(range: "today" | "7d" | "30d",
    accountEnvironment: "simulation" | "real", currency?: string): Promise<ForexOperationsOverviewPerformance> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/performance`,
      { params: { range, account_environment: accountEnvironment, ...(currency ? { currency } : {}) } }));
  },

  async getOperationsOverviewFollowObserved(range: "today" | "7d" | "30d"): Promise<ForexOperationsOverviewFollowObserved> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/follow-observed`,
      { params: { range } }));
  },

  async getOperationsOverviewDecisionObserved(): Promise<ForexOperationsOverviewDecisionObserved> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/decision-observed`));
  },

  async getOperationsOverviewMarketObserved(): Promise<ForexOperationsOverviewMarketObserved> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/market-observed`));
  },

  async getOperationsOverviewExecutionObserved(): Promise<ForexOperationsOverviewExecutionObserved> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/execution-observed`));
  },

  async getOperationsOverviewAgents(): Promise<ForexOperationsOverviewAgentPage> {
    return unwrap(await api.get(`${FOREX_PREFIX}/dashboards/operations-overview/agents`, { params: { page: 1, page_size: 10 } }));
  },

  async getOperationsOverviewTodos(): Promise<ForexOperatorTodoPage> {
    return unwrap(await api.get(`${FOREX_PREFIX}/operator-todos`, { params: { limit: 10 } }));
  },
  async getTestAccountAccess(accountId: string): Promise<{
    item: TestAccountAccess | null; can_manage: boolean;
  }> {
    const result = unwrap<{ items: TestAccountAccess[]; can_manage: boolean }>(await api.get(
      `${FOREX_PREFIX}/v2/test-account-allowlist`, { params: { account_id: accountId } }));
    return { item: result.items[0] ?? null, can_manage: result.can_manage === true };
  },

  async putTestAccountAccess(accountId: string, enabled: boolean,
                             expectedRevision: number, requestId: string): Promise<{
    follower_account_id: string; owner_scope: string; enabled: boolean; revision: number; updated_at: number;
  }> {
    return unwrap(await api.put(`${FOREX_PREFIX}/v2/test-account-allowlist/${encodeURIComponent(accountId)}`,
      { enabled, expected_revision: expectedRevision },
      { headers: { "Idempotency-Key": requestId } }));
  },

  async listConfigTemplates(kind: "strategy"): Promise<{ items: ConfigTemplateV1[] }> {
    const response = await api.get(`${FOREX_PREFIX}/v2/templates`, { params: { kind } });
    return unwrap(response);
  },

  async createConfigTemplate(kind: "strategy", name: string,
                             payload: Record<string, unknown>, requestId: string,
                             confirmedInPage = false): Promise<ConfigTemplateV1> {
    const token = await strategyConfirmation(confirmedInPage);
    const response = await api.post(`${FOREX_PREFIX}/v2/templates/${kind}`, { name, payload }, {
      headers: { "Idempotency-Key": requestId,
                 ...(token ? { "X-Risk-Confirmation-Token": token } : {}) },
    });
    return unwrap(response);
  },

  async recordStrategyQualification(templateId: string, version: number,
      report: Record<string, unknown>, requestId: string): Promise<Record<string, unknown>> {
    const token = await strategyConfirmation(true);
    return unwrap(await api.post(`${FOREX_PREFIX}/v2/templates/${templateId}/qualifications`, { version, report }, {
      headers: { "Idempotency-Key": requestId, ...(token ? { "X-Risk-Confirmation-Token": token } : {}) },
    }));
  },

  async listConfigTemplateRevisions(templateId: string, beforeRevision?: number): Promise<{
    items: Array<{ template_ref: ImmutableArtifactRef; created_at: number; created_by: string }>;
    current_revision: number; next_before_revision: number | null;
  }> {
    return unwrap(await api.get(`${FOREX_PREFIX}/v2/templates/${templateId}/revisions`,
      { params: { limit: 20, before_revision: beforeRevision } }));
  },

  async getConfigTemplateRevision(templateId: string, version: number): Promise<ConfigTemplateV1> {
    return unwrap(await api.get(`${FOREX_PREFIX}/v2/templates/${templateId}/revisions/${version}`));
  },

  async reviseConfigTemplate(template: ConfigTemplateV1, name: string,
                              payload: Record<string, unknown>, requestId: string,
                              confirmedInPage = false): Promise<ConfigTemplateV1> {
    const token = await strategyConfirmation(confirmedInPage);
    return unwrap(await api.post(`${FOREX_PREFIX}/v2/templates/${template.kind}/${template.template_ref.id}/revisions`,
      { expected_revision: template.current_revision ?? template.template_ref.version, name, payload },
      { headers: { "Idempotency-Key": requestId, ...(token ? { "X-Risk-Confirmation-Token": token } : {}) } }));
  },

  async previewStrategyRequirements(payload: StrategyHistoryRequest, signal?: AbortSignal): Promise<StrategyHistoryPreview> {
    const response = await api.post(`${FOREX_PREFIX}/v2/strategy-requirements`, payload, { signal });
    return unwrap<StrategyHistoryPreview>(response);
  },

  async getConfigCatalog(currency = "USD"): Promise<ConfigCatalogV1> {
    const response = await api.get(`${FOREX_PREFIX}/v2/catalog`, { params: { currency } });
    return unwrap<ConfigCatalogV1>(response);
  },

  async composeConfigAgent(payload: ConfigCompositionV1): Promise<Record<string, unknown>> {
    const response = await api.post(`${FOREX_PREFIX}/v2/compose`, payload);
    return unwrap<Record<string, unknown>>(response);
  },

  async listStrategySignals(agentId?: string, cursor?: string | null): Promise<StrategySignalList> {
    const response = await api.get(`${FOREX_PREFIX}/strategy-signals`, { params: {
      ...(agentId ? { agent_id: agentId } : {}), ...(cursor ? { cursor } : {}),
    } });
    return unwrap<StrategySignalList>(response);
  },

  async listAccountCommands(terminalId?: string, cursor?: string | null, scope?: {accountScope?: string; agentId?: string}): Promise<{items: AccountCommand[]; next_cursor: string | null}> {
    return unwrap(await api.get(`${FOREX_PREFIX}/account-commands`, { params: {
      ...(terminalId ? { terminal_id: terminalId } : {}), ...(cursor ? { cursor } : {}),
      ...(scope?.accountScope ? { account_scope: scope.accountScope } : {}),
      ...(scope?.agentId ? { agent_id: scope.agentId } : {}),
    } }));
  },

  async getAccountCommandSummary(): Promise<{total: number; status_counts: Record<string, number>}> {
    return unwrap(await api.get(`${FOREX_PREFIX}/account-commands/summary`));
  },

  async getAccountCommand(commandId: string): Promise<AccountCommand> {
    return unwrap(await api.get(`${FOREX_PREFIX}/account-commands/${encodeURIComponent(commandId)}`));
  },

  async getStrategySignal(signalId: string, agentId?: string): Promise<MasterSignalDetail> {
    const response = await api.get(`${FOREX_PREFIX}/strategy-signals/${encodeURIComponent(signalId)}`,
      { params: agentId ? { agent_id: agentId } : {} });
    return unwrap<MasterSignalDetail>(response);
  },

  async createConfigAgent(name: string, config: Record<string, unknown>, requestId: string,
    startValidation = false): Promise<{ agent_id: string; review_id?: string; runtime?: string }> {
    const target = config.platform_execution_target as { account_scope: string; terminal_ref: string };
    const response = await api.post(`${FOREX_PREFIX}/v2/agents`, {
      name, master_account_id: target.account_scope, terminal_id: target.terminal_ref, config,
      ...(startValidation ? { creation_action: "create_and_validate" } : {}),
    }, { headers: { "Idempotency-Key": requestId } });
    return unwrap(response);
  },

  async getConfigDraft(agentId: string): Promise<{ draft_revision: number; payload: Record<string, unknown>; content_hash: string }> {
    const response = await api.get(`${FOREX_PREFIX}/v2/agents/${agentId}/draft`);
    return unwrap(response);
  },

  async saveConfigDraft(agentId: string, revision: number, config: Record<string, unknown>, requestId: string): Promise<{ draft_revision: number; content_hash: string }> {
    const response = await api.put(`${FOREX_PREFIX}/v2/agents/${agentId}/draft`, {
      expected_draft_revision: revision, config,
    }, { headers: { "Idempotency-Key": requestId } });
    return unwrap(response);
  },

  async submitConfigAgent(agentId: string, revision: number, requestId: string, startValidation = false): Promise<{ review_id: string; config_id: string; binding_id?: string; runtime?: string }> {
    const response = await api.post(`${FOREX_PREFIX}/v2/agents/${agentId}/reviews`, {
      expected_draft_revision: revision, start_validation: startValidation,
    }, { headers: { "Idempotency-Key": requestId } });
    return unwrap(response);
  },
  async listConfigReviews(status?: string): Promise<ListResponse<ConfigReviewV1>> {
    const response = await api.get(`${FOREX_PREFIX}/v2/reviews`, { params: { status } });
    return unwrap<ListResponse<ConfigReviewV1>>(response);
  },

  async getConfigReview(reviewId: string): Promise<ConfigReviewV1> {
    const response = await api.get(`${FOREX_PREFIX}/v2/reviews/${reviewId}`);
    return unwrap<ConfigReviewV1>(response);
  },

  async decideConfigReview(review: ConfigReviewV1, decision: "approve" | "reject", requestId: string): Promise<{
    status: "approved" | "rejected"; active_config_id: string | null;
  }> {
    const response = await api.post(`${FOREX_PREFIX}/v2/reviews/${review.review_id}/decision`, {
      decision,
      expected_draft_revision: review.source_draft_revision,
      expected_active_config_id: review.expected_active_config_id,
      reason: decision === "reject" ? "审核未通过" : null,
    }, { headers: { "Idempotency-Key": requestId } });
    return unwrap(response);
  },

  async listConfigAgents(): Promise<ListResponse<ConfigAgentV1>> {
    const response = await api.get(`${FOREX_PREFIX}/v2/agents`);
    return unwrap<ListResponse<ConfigAgentV1>>(response);
  },

  async getConfigAgent(agentId: string): Promise<ConfigAgentV1> {
    const response = await api.get(`${FOREX_PREFIX}/v2/agents/${agentId}`);
    return unwrap<ConfigAgentV1>(response);
  },

  async renameConfigAgent(agentId: string, name: string, rowRevision: number, requestId: string): Promise<{
    agent_id: string; name: string; row_revision: number; changed: boolean;
  }> {
    const response = await api.patch(`${FOREX_PREFIX}/v2/agents/${agentId}/name`, {
      name, expected_row_revision: rowRevision,
    }, { headers: { "Idempotency-Key": requestId } });
    return unwrap(response);
  },

  async setConfigAgentRuntime(agentId: string, desiredState: "running" | "stopped",
                              controlRevision: number, requestId: string): Promise<{
    agent_id: string; desired_state: "running" | "stopped"; control_revision: number;
  }> {
    const response = await api.post(`${FOREX_PREFIX}/v2/agents/${agentId}/runtime`, {
      desired_state: desiredState, expected_control_revision: controlRevision,
    }, { headers: { "Idempotency-Key": requestId } });
    return unwrap(response);
  },

  async getConfigAgentDeletionPreview(agentId: string): Promise<ConfigAgentDeletionPreview> {
    const response = await api.get(`${FOREX_PREFIX}/v2/agents/${agentId}/deletion-preview`);
    return unwrap<ConfigAgentDeletionPreview>(response);
  },

  async deleteConfigAgent(agentId: string, revision: number, reason: string, requestId: string): Promise<{
    agent_id: string; status: "deleted" | "draining"; operation_id?: string; unbound_account_id?: string;
  }> {
    const response = await api.delete(`${FOREX_PREFIX}/v2/agents/${agentId}`, {
      data: { expected_row_revision: revision, reason },
      headers: { "Idempotency-Key": requestId },
    });
    return unwrap(response);
  },

  async getConfigOperation(operationId: string): Promise<{
    status: "applied" | "draining"; result: { status: "deleted" | "draining" };
  }> {
    const response = await api.get(`${FOREX_PREFIX}/v2/operations/${operationId}`);
    return unwrap(response);
  },
  async listAtomicModels(params?: { enabledOnly?: boolean }): Promise<ListResponse<AtomicModel>> {
    const response = await api.get(`${FOREX_PREFIX}/atomic-models`, {
      params: { enabled_only: params?.enabledOnly ?? false },
    });
    return unwrap<ListResponse<AtomicModel>>(response);
  },

  async listRegisteredDataSources(): Promise<ListResponse<RegisteredDataSource>> {
    const response = await api.get(`${FOREX_PREFIX}/registered-data-sources`);
    return unwrap<ListResponse<RegisteredDataSource>>(response);
  },

  async listMasterSignals(agentId: string): Promise<{ items: MasterSignalListItem[]; next_cursor?: string | null }> {
    const response = await api.get(`${FOREX_PREFIX}/platform-agents/${agentId}/master-signals`);
    return response.data.data;
  },

  async listMasterSignalRuns(agentId: string): Promise<{ items: MasterSignalRunSummary[] }> {
    const response = await api.get(`${FOREX_PREFIX}/platform-agents/${agentId}/master-signal-runs`);
    return unwrap<{ items: MasterSignalRunSummary[] }>(response);
  },

  async getMasterSignalRunDetail(agentId: string, runId: string): Promise<MasterSignalRunDetail> {
    const response = await api.get(`${FOREX_PREFIX}/platform-agents/${agentId}/master-signal-runs/${runId}`);
    return unwrap<MasterSignalRunDetail>(response);
  },

  async getMasterSignalDetail(agentId: string, signalId: string): Promise<MasterSignalDetail> {
    const response = await api.get(`${FOREX_PREFIX}/platform-agents/${agentId}/master-signals/${signalId}`);
    return response.data.data;
  },

  async listFollowerAccountReviews(): Promise<ListResponse<FollowerAccountReview>> {
    const response = await api.get(`${FOREX_PREFIX}/follower-account-reviews`);
    return unwrap<ListResponse<FollowerAccountReview>>(response);
  },

  async patchFollowerAccountReview(reviewId: string, payload: FollowerReviewPatchPayload, operationKey: string = crypto.randomUUID()): Promise<FollowerAccountReview> {
    const response = await api.patch(`${FOREX_PREFIX}/follower-account-reviews/${reviewId}`, payload, { headers: { "Idempotency-Key": operationKey } });
    return unwrap<FollowerAccountReview>(response);
  },

  async createProvisioningJob(payload: ProvisioningJobCreatePayload, operationKey: string = crypto.randomUUID()): Promise<ProvisioningJob> {
    const response = await api.post(`${FOREX_PREFIX}/provisioning/jobs`, payload, { headers: { "Idempotency-Key": operationKey } });
    return unwrap<ProvisioningJob>(response);
  },

  async onboardingOptions(accountRole?: "master" | "follower"): Promise<{ catalog_version: string; consent_version: string; servers: Array<{broker: string; server: string; environment: string; readiness?: 'template_listed'; reason_code?: 'RUNTIME_ADMISSION_DECIDED_PER_JOB'; owner?: 'platform'}>; platform_servers?: Array<{platform:'mt4'|'mt5';broker:string;server:string;environment:string;readiness?:'template_listed';reason_code?:'RUNTIME_ADMISSION_DECIDED_PER_JOB';owner?:'platform'}> }> {
    return unwrap(await api.get(`${FOREX_PREFIX}/ea-onboarding/options`, {params: accountRole ? {account_role: accountRole} : undefined}));
  },
  async storeMasterCredentials(id: string, payload: {login: string; password: string; consent_version: string; expected_credential_version: number | null}, operationKey: string): Promise<{credential_binding_id: string; version: number; status: string}> {
    return unwrap(await api.put(`${FOREX_PREFIX}/master-accounts/${id}/credentials`, payload, {headers: {"Idempotency-Key": operationKey}}));
  },
  async managedJobAction(jobId: string, action: "retry" | "cancel", revision: number): Promise<ProvisioningJob> {
    const request = managedOperationKey(jobId, action, revision);
    const result = unwrap<ProvisioningJob>(await api.post(`${FOREX_PREFIX}/provisioning/jobs/${jobId}/${action}`, {expected_revision: revision, reason_code: null}, {headers: {"Idempotency-Key": request.key}}));
    request.complete();
    return result;
  },

  async listProvisioningJobs(params?: { target_type?: string; target_id?: string; status?: string; instance_scope?: "new" | "historical"; account_role?: string; search?: string; instance_state?: string; page?: number; page_size?: number }): Promise<ListResponse<ProvisioningJob>> {
    const response = await api.get(`${FOREX_PREFIX}/provisioning/jobs`, { params });
    return unwrap<ListResponse<ProvisioningJob>>(response);
  },

  async getProvisioningJob(jobId: string,params?:{events_cursor?:number;events_limit?:number;events_page_token?:string}): Promise<ProvisioningJob> {
    return unwrap(await api.get(`${FOREX_PREFIX}/provisioning/jobs/${encodeURIComponent(jobId)}`, {params}));
  },

  async maintainManagedTerminal(runnerId: string, jobId: string, revision: number, action: InstanceAction, operationKey: string, extra?: {expected_source_revision?: number | null; expected_collector_revision?: number | null; operation_id?: string}): Promise<{runner_id:string;action:string;jobs:Array<{job_id:string;terminal_id:string;revision:number;state:string}>}> {
    const response = await api.post(`${FOREX_PREFIX}/runners/${encodeURIComponent(runnerId)}/maintenance`,
      {action, expected_revisions:{[jobId]:revision}, selected_job_ids:[jobId], ...extra}, {headers:{"Idempotency-Key":operationKey}});
    return unwrap(response);
  },

  async completeProvisioningJob(jobId: string): Promise<{ job: ProvisioningJob; target: Record<string, unknown> }> {
    const response = await api.post(`${FOREX_PREFIX}/provisioning/jobs/${jobId}/complete`);
    return unwrap<{ job: ProvisioningJob; target: Record<string, unknown> }>(response);
  },

  async approveProvisioningSimulationAttestation(jobId: string, payload: ProvisioningSimulationAttestationPayload): Promise<ProvisioningJob> {
    const response = await api.post(`${FOREX_PREFIX}/provisioning/jobs/${jobId}/simulation-attestation`, payload);
    return unwrap<ProvisioningJob>(response);
  },

  async listMasterAccounts(): Promise<ListResponse<MasterAccount>> {
    const response = await api.get(`${FOREX_PREFIX}/master-accounts`);
    return unwrap<ListResponse<MasterAccount>>(response);
  },

  async createMasterAccount(payload: MasterAccountCreatePayload, operationKey?: string): Promise<MasterAccount> {
    const response = await api.post(`${FOREX_PREFIX}/master-accounts`, payload, operationKey ? {headers: {"Idempotency-Key": operationKey}} : undefined);
    return unwrap<MasterAccount>(response);
  },

  async verifyMasterAccount(payload: MasterAccountVerifyPayload): Promise<MasterAccountVerifyResult> {
    const response = await api.post(`${FOREX_PREFIX}/master-accounts/verify`, payload);
    return unwrap<MasterAccountVerifyResult>(response);
  },

  async patchMasterAccount(accountId: string, payload: MasterAccountPatchPayload): Promise<MasterAccount> {
    const response = await api.patch(`${FOREX_PREFIX}/master-accounts/${accountId}`, payload);
    return unwrap<MasterAccount>(response);
  },

  async listMasterOrders(params: {
    masterAccountId: string;
    lifecycleStatus?: string;
    symbol?: string;
    dateFrom?: number;
    dateTo?: number;
    timeBasis?: "broker_raw" | "utc_v1";
    cursor?: string;
    limit?: number;
  }): Promise<MasterTradeOrderPage> {
    const response = await api.get(`${FOREX_PREFIX}/master-orders`, {
      params: {
        master_account_id: params.masterAccountId,
        lifecycle_status: params.lifecycleStatus || undefined,
        symbol: params.symbol || undefined,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        time_basis: params.timeBasis,
        cursor: params.cursor,
        limit: params.limit ?? 50,
      },
    });
    return unwrap<MasterTradeOrderPage>(response);
  },

  async getMasterOrder(orderRef: string): Promise<MasterTradeTrace> {
    const response = await api.get(`${FOREX_PREFIX}/master-orders/${encodeURIComponent(orderRef)}`);
    return unwrap<MasterTradeTrace>(response);
  },

  async listTerminalSlots(): Promise<ListResponse<TerminalSlot>> {
    const response = await api.get(`${FOREX_PREFIX}/terminal-slots`);
    return unwrap<ListResponse<TerminalSlot>>(response);
  },

  async patchTerminalSlot(terminalId: string, payload: TerminalPatchPayload): Promise<TerminalSlot> {
    const response = await api.patch(`${FOREX_PREFIX}/terminal-slots/${terminalId}`, payload);
    return unwrap<TerminalSlot>(response);
  },

  async listRiskConfigs(): Promise<ListResponse<RiskConfig>> {
    const response = await api.get(`${FOREX_PREFIX}/risk-configs`);
    return unwrap<ListResponse<RiskConfig>>(response);
  },

  async patchRiskConfig(riskConfigId: string, payload: RiskConfigPatchPayload): Promise<RiskConfig> {
    const response = await api.patch(`${FOREX_PREFIX}/risk-configs/${riskConfigId}`, payload);
    return unwrap<RiskConfig>(response);
  },

  async listConfigReleases(): Promise<ListResponse<ConfigRelease>> {
    const response = await api.get(`${FOREX_PREFIX}/config-releases`);
    return unwrap<ListResponse<ConfigRelease>>(response);
  },

  async createConfigRelease(payload: ConfigReleaseCreatePayload): Promise<ConfigRelease> {
    const response = await api.post(`${FOREX_PREFIX}/config-releases`, payload);
    return unwrap<ConfigRelease>(response);
  },

  async rollbackConfigRelease(releaseId: string): Promise<ConfigRelease> {
    const response = await api.post(`${FOREX_PREFIX}/config-releases/${releaseId}/rollback`);
    return unwrap<ConfigRelease>(response);
  },

  async listConfigPackages(params?: { enabledOnly?: boolean }): Promise<ListResponse<ConfigPackage>> {
    const response = await api.get(`${FOREX_PREFIX}/config-packages`, {
      params: { enabled_only: params?.enabledOnly ?? false },
    });
    return unwrap<ListResponse<ConfigPackage>>(response);
  },

  async getConfigPackage(configKey: string): Promise<ConfigPackage> {
    const response = await api.get(`${FOREX_PREFIX}/config-packages/${configKey}`);
    return unwrap<ConfigPackage>(response);
  },

  async patchConfigPackage(configKey: string, payload: ConfigPackagePatchPayload): Promise<ConfigPackage> {
    const response = await api.patch(`${FOREX_PREFIX}/config-packages/${configKey}`, payload);
    return unwrap<ConfigPackage>(response);
  },

  async publishConfigPackage(configKey: string, payload: ConfigPackagePublishPayload): Promise<ConfigPackagePublishResult> {
    const response = await api.post(`${FOREX_PREFIX}/config-packages/${configKey}/publish`, payload);
    return unwrap<ConfigPackagePublishResult>(response);
  },

  async rollbackConfigPackage(configKey: string, releaseId: string): Promise<ConfigPackagePublishResult> {
    const response = await api.post(`${FOREX_PREFIX}/config-packages/${configKey}/rollback/${releaseId}`);
    return unwrap<ConfigPackagePublishResult>(response);
  },

  async patchKillSwitch(payload: KillSwitchPatchPayload): Promise<KillSwitchResult> {
    const response = await api.post(`${FOREX_PREFIX}/kill-switch`, payload);
    return unwrap<KillSwitchResult>(response);
  },

  async listFollowRelations(
    params?: { status?: string },
    options?: { signal?: AbortSignal; timeout?: number },
  ): Promise<ListResponse<FollowRelation>> {
    const response = await api.get(`${FOREX_PREFIX}/follow-relations`, { ...options, params });
    return unwrap<ListResponse<FollowRelation>>(response);
  },

  async patchFollowRelation(relationId: string, payload: FollowRelationPatchPayload): Promise<FollowRelation> {
    const response = await api.patch(`${FOREX_PREFIX}/follow-relations/${relationId}`, payload);
    return unwrap<FollowRelation>(response);
  },

  async getExecutionReadiness(terminalId:string):Promise<EAExecutionReadiness> {
    const response=await api.get(`${FOREX_PREFIX}/terminals/${encodeURIComponent(terminalId)}/execution-readiness`);
    return response.data.data;
  },

  async listAccountExecutionIssues(cursor?: string | null, terminalOnly = false): Promise<{items: AccountExecutionIssue[]; next_cursor: string | null}> {
    const response = await api.get(`${FOREX_PREFIX}/account-execution-issues`, {params: {limit: 50, terminal_only: terminalOnly, ...(cursor ? {cursor} : {})}});
    return unwrap<{items: AccountExecutionIssue[]; next_cursor: string | null}>(response);
  },
  async activateTerminalExecution(terminalId:string,payload:{expected_revision:number},idempotencyKey:string) {
    const response=await api.post(`${FOREX_PREFIX}/terminals/${encodeURIComponent(terminalId)}/execution-activation`,payload,{headers:{'Idempotency-Key':idempotencyKey}});
    return response.data.data as {operation_id:string;state:string};
  },



  async listAuditLogs(): Promise<ListResponse<ForexAuditLog>> {
    const response = await api.get(`${FOREX_PREFIX}/audit-logs`);
    return unwrap<ListResponse<ForexAuditLog>>(response);
  },

  async createOperatorOwner(payload: { name: string; contact_person: string; node_type: 'company' | 'team'; parent_owner_ref?: string }, key: string): Promise<OperatorOwner> {
    const response = await api.post(`${FOREX_PREFIX}/operator-owners`, payload, { headers: { 'Idempotency-Key': key } });
    return (response.data as Envelope<OperatorOwner>).data;
  },

  async listOperatorOwners(): Promise<ListResponse<OperatorOwner>> {
    const response = await api.get(`${FOREX_PREFIX}/operator-owners`);
    return unwrap<ListResponse<OperatorOwner>>(response);
  },

  async getOperatorOwnerInvitation(ownerRef: string): Promise<{ owner_ref: string; code: string; status: string; company_ref: string }> {
    const response = await api.get(`${FOREX_PREFIX}/operator-owners/${encodeURIComponent(ownerRef)}/invitation`);
    return unwrap<{ owner_ref: string; code: string; status: string; company_ref: string }>(response);
  },

  async getOperatorOwnerRegistrations(ownerRef: string, scope: 'owner' | 'company'): Promise<{ total: number; items: Array<{ user_id: string; email_masked: string | null; owner_ref: string; company_ref: string; path_names: string[]; recorded_at: number }> }> {
    const response = await api.get(`${FOREX_PREFIX}/operator-owners/${encodeURIComponent(ownerRef)}/registrations`, { params: { scope } });
    return unwrap<{ total: number; items: Array<{ user_id: string; email_masked: string | null; owner_ref: string; company_ref: string; path_names: string[]; recorded_at: number }> }>(response);
  },

  async listTeamMembers(params?: { owner_ref?: string; company_ref?: string }): Promise<ListResponse<TeamMember>> {
    return unwrap<ListResponse<TeamMember>>(await api.get(`${FOREX_PREFIX}/team-members`, { params }));
  },

  async createTeamMember(payload: { owner_ref: string; member_no: string; display_name: string }, key: string): Promise<TeamMember> {
    return unwrap<TeamMember>(await api.post(`${FOREX_PREFIX}/team-members`, payload, { headers: { 'Idempotency-Key': key } }));
  },

  async setTeamMemberStatus(memberRef: string, status: 'active' | 'inactive', expectedRevision: number, reason: string): Promise<TeamMember> {
    return unwrap<TeamMember>(await api.patch(`${FOREX_PREFIX}/team-members/${encodeURIComponent(memberRef)}`, {
      status, expected_revision: expectedRevision, reason,
    }));
  },

  async rotateTeamMemberCode(memberRef: string, expectedRevision: number, key: string, reason: string): Promise<TeamMember> {
    return unwrap<TeamMember>(await api.post(`${FOREX_PREFIX}/team-members/${encodeURIComponent(memberRef)}/invitation/rotate`,
      { expected_revision: expectedRevision, reason }, { headers: { 'Idempotency-Key': key } }));
  },

  async listInvitedUsers(params: { member_ref?: string; owner_ref?: string; company_ref?: string; include_descendants?: boolean; cursor?: string; from_ms?: number; to_ms?: number; limit?: number }): Promise<ListResponse<InvitedUser> & { has_more: boolean; next_cursor: string | null; observed_at: number }> {
    return unwrap<ListResponse<InvitedUser> & { has_more: boolean; next_cursor: string | null; observed_at: number }>(await api.get(`${FOREX_PREFIX}/invited-users`, { params }));
  },

  async getRegistrationSourceDetail(userId: string): Promise<{ user_id: string; email_masked: string | null; nickname: string | null; source: Record<string, unknown> }> {
    return unwrap<{ user_id: string; email_masked: string | null; nickname: string | null; source: Record<string, unknown> }>(
      await api.get(`${FOREX_PREFIX}/registration-sources/${encodeURIComponent(userId)}`));
  },

  async patchOperatorOwner(ownerRef: string, payload: OperatorOwnerPatchPayload): Promise<OperatorOwner> {
    const response = await api.patch(`${FOREX_PREFIX}/operator-owners/${ownerRef}`, payload);
    return unwrap<OperatorOwner>(response);
  },

  async listModelInvocationLogs(params?: { status?: string }): Promise<ListResponse<ModelInvocationLog>> {
    const response = await api.get(`${FOREX_PREFIX}/model-invocation-logs`, { params });
    return unwrap<ListResponse<ModelInvocationLog>>(response);
  },

  async getProfitDashboard(): Promise<ForexDashboard<Record<string, unknown>>> {
    const response = await api.get(`${FOREX_PREFIX}/dashboards/profit`);
    return unwrap<ForexDashboard<Record<string, unknown>>>(response);
  },

  async getModelDashboard(): Promise<ForexDashboard<Record<string, unknown>>> {
    const response = await api.get(`${FOREX_PREFIX}/dashboards/models`);
    return unwrap<ForexDashboard<Record<string, unknown>>>(response);
  },

};
